import { MAX_UPLOAD_FILES_PER_SESSION } from '@/utils/uploadSessionLimits';
import { NAV_HUB_CARD_IMAGES } from './navHubCardImages';
import { TOOL_NAV_ITEMS } from './workbenchNavConfig';

export const MODEL_STUDIO_NAV_IDS = [
  'modelTryOn',
  'modelHandheld',
  'modelPoseFission',
  'modelUniversalWear',
];

export const MODEL_STUDIO_MAX_SOURCES = MAX_UPLOAD_FILES_PER_SESSION;
export const MODEL_STUDIO_MAX_BATCH_IMAGES = MAX_UPLOAD_FILES_PER_SESSION;
export const MODEL_TRY_ON_MAX_GARMENTS = MODEL_STUDIO_MAX_BATCH_IMAGES;
export const MODEL_HANDHELD_MAX_PRODUCTS = MODEL_STUDIO_MAX_BATCH_IMAGES;
export const MODEL_POSE_FISSION_MAX_MODELS = MAX_UPLOAD_FILES_PER_SESSION;
export const MODEL_UNIVERSAL_WEAR_MAX_GARMENTS = MODEL_STUDIO_MAX_BATCH_IMAGES;
/** 寻源工作流等批处理并发槽（与上传上限解耦，避免一次打满 500 路） */
export const MODEL_STUDIO_BATCH_CONCURRENT = 6;
export const MODEL_STUDIO_PROMPT_MAX_LENGTH = 500;
export const MODEL_STUDIO_GENERATE_COUNT_OPTIONS = [1, 2, 3, 4];

/** 模特试衣 · 衣物细分品类（联动取景范围与提示词） */
export const MODEL_TRY_ON_GARMENT_TYPE_OPTIONS = [
  { value: 'jeansJogger', label: '牛仔裤(束脚)', category: 'lowerBody', frameScope: 'fullBody' },
  { value: 'longSleeveTee', label: 'T恤长袖', category: 'upperBody', frameScope: 'upperHalf' },
  { value: 'shortSleeveTee', label: 'T恤短袖', category: 'upperBody', frameScope: 'upperHalf' },
  { value: 'hoodie', label: '卫衣', category: 'upperBody', frameScope: 'upperHalf' },
  { value: 'longSleeveShirt', label: '长袖衬衫', category: 'upperBody', frameScope: 'upperHalf' },
  { value: 'shortDownJacket', label: '短款羽绒服', category: 'upperBody', frameScope: 'fullBody' },
  { value: 'longDownJacket', label: '长款羽绒服', category: 'fullBody', frameScope: 'fullBody' },
  { value: 'jeansWide', label: '牛仔裤(宽筒)', category: 'lowerBody', frameScope: 'fullBody' },
  { value: 'croppedPants', label: '九分裤', category: 'lowerBody', frameScope: 'fullBody' },
  { value: 'cargoPantsLoose', label: '工装裤(宽松)', category: 'lowerBody', frameScope: 'fullBody' },
  { value: 'cargoPantsJogger', label: '工装裤(束脚)', category: 'lowerBody', frameScope: 'fullBody' },
  { value: 'sportsShorts', label: '运动短裤', category: 'lowerBody', frameScope: 'lowerHalf' },
  { value: 'capriPants', label: '七分裤', category: 'lowerBody', frameScope: 'fullBody' },
  { value: 'thermalUnderwear', label: '秋衣秋裤', category: 'fullBody', frameScope: 'fullBody' },
  { value: 'teeShirt', label: 'T恤衫', category: 'upperBody', frameScope: 'upperHalf' },
  { value: 'shortSleeveShirt', label: '短袖衬衫', category: 'upperBody', frameScope: 'upperHalf', modelTypes: ['female', 'male'] },
  { value: 'mensSweater', label: '男士毛衣', category: 'upperBody', frameScope: 'upperHalf', modelTypes: ['male', 'boy'] },
  { value: 'mensTights', label: '男士紧身裤', category: 'lowerBody', frameScope: 'fullBody', modelTypes: ['male', 'boy'] },
  { value: 'suit', label: '西装', category: 'fullBody', frameScope: 'fullBody' },
  { value: 'dress', label: '连衣裙', category: 'dress', frameScope: 'fullBody', modelTypes: ['female', 'girl'] },
];

export const DEFAULT_MODEL_TRY_ON_GARMENT_TYPE = 'jeansJogger';

/** 当前模特性别下是否可选该衣物类型 */
export function isGarmentTypeAllowedForModel(garmentType, modelType) {
  const matched = MODEL_TRY_ON_GARMENT_TYPE_OPTIONS.find((opt) => opt.value === garmentType);
  if (!matched?.modelTypes?.length) return true;
  return matched.modelTypes.includes(modelType);
}

/** 按模特性别过滤衣物类型选项 */
export function resolveGarmentTypeOptionsForModel(modelType) {
  return MODEL_TRY_ON_GARMENT_TYPE_OPTIONS.filter(
    (opt) => !opt.modelTypes?.length || opt.modelTypes.includes(modelType),
  );
}

/** 模特性别切换时回退到该性别下的默认衣物类型 */
export function resolveDefaultGarmentTypeForModel(modelType) {
  const options = resolveGarmentTypeOptionsForModel(modelType);
  if (options.some((opt) => opt.value === DEFAULT_MODEL_TRY_ON_GARMENT_TYPE)) {
    return DEFAULT_MODEL_TRY_ON_GARMENT_TYPE;
  }
  return options[0]?.value || DEFAULT_MODEL_TRY_ON_GARMENT_TYPE;
}

/** 按模特性别校正衣物类型与取景范围（与模特试衣页逻辑一致） */
export function sanitizeModelTryOnParams(params = {}) {
  const modelType = String(params.modelType || DEFAULT_MODEL_TRY_ON_MODEL_TYPE).trim() || DEFAULT_MODEL_TRY_ON_MODEL_TYPE;
  const rawGarment = String(params.garmentType || DEFAULT_MODEL_TRY_ON_GARMENT_TYPE).trim() || DEFAULT_MODEL_TRY_ON_GARMENT_TYPE;
  const garmentType = isGarmentTypeAllowedForModel(rawGarment, modelType)
    ? rawGarment
    : resolveDefaultGarmentTypeForModel(modelType);
  const frameScope = resolveTryOnFrameScopeForGarment(garmentType, params.frameScope);
  return { modelType, garmentType, frameScope };
}

/** 将细分品类映射为粗分类（兼容历史 upperBody / lowerBody 等值） */
export function resolveGarmentCategory(garmentType) {
  const matched = MODEL_TRY_ON_GARMENT_TYPE_OPTIONS.find((opt) => opt.value === garmentType);
  if (matched?.category) return matched.category;
  if (['upperBody', 'lowerBody', 'dress', 'fullBody'].includes(garmentType)) return garmentType;
  return 'upperBody';
}

/** 模特试衣 · 取景范围（上半身 / 下半身 / 全身） */
export const MODEL_TRY_ON_FRAME_SCOPE_OPTIONS = [
  { value: 'upperHalf', label: '上半身' },
  { value: 'lowerHalf', label: '下半身' },
  { value: 'fullBody', label: '全身' },
];

export const DEFAULT_MODEL_TRY_ON_FRAME_SCOPE = 'fullBody';

/** 衣物类型变更时联动推荐取景范围 */
export function resolveTryOnFrameScopeForGarment(garmentType, currentFrameScope) {
  const matched = MODEL_TRY_ON_GARMENT_TYPE_OPTIONS.find((opt) => opt.value === garmentType);
  if (matched?.frameScope) return matched.frameScope;

  const category = resolveGarmentCategory(garmentType);
  const current = currentFrameScope || DEFAULT_MODEL_TRY_ON_FRAME_SCOPE;
  if (category === 'lowerBody') {
    return current === 'upperHalf' ? 'lowerHalf' : current;
  }
  if (category === 'upperBody') {
    if (current === 'lowerHalf') return 'upperHalf';
    return current;
  }
  if (category === 'dress' || category === 'fullBody') return 'fullBody';
  return current;
}

/** 模特试衣 · 模特类型 */
export const MODEL_TRY_ON_MODEL_TYPE_OPTIONS = [
  { value: 'female', label: '女模特' },
  { value: 'male', label: '男模特' },
  { value: 'girl', label: '女童模特' },
  { value: 'boy', label: '男童模特' },
];

export const DEFAULT_MODEL_TRY_ON_MODEL_TYPE = 'female';

/** 模特试衣 · 人种选择 */
export const MODEL_TRY_ON_ETHNICITY_OPTIONS = [
  { value: 'asian', label: '黄种人' },
  { value: 'white', label: '白人' },
  { value: 'black', label: '黑人' },
];

export const DEFAULT_MODEL_TRY_ON_ETHNICITY = 'asian';

export function isModelStudioNav(navId) {
  return MODEL_STUDIO_NAV_IDS.includes(navId);
}

const MODEL_STUDIO_BASE = {
  modelTryOn: {
    title: '模特试衣',
    subtitle: '上传服饰图并选择模特，AI 虚拟试穿省去实拍成本',
    generateLabel: '立即试衣',
    legalNote: '保持服装款式与细节一致，生成自然试穿效果',
    layout: 'tryOn',
    uploadSlots: [
      { id: 'garment', label: '服饰图', hint: `平铺或挂拍服装图，最多 ${MODEL_TRY_ON_MAX_GARMENTS} 张`, required: true, max: MODEL_TRY_ON_MAX_GARMENTS, placement: 'workspace' },
    ],
    sidebarSlots: [
      { id: 'model', label: '模特图', hint: '也可在下方选择系统模特', required: false, max: 1, placement: 'sidebar' },
    ],
    showModelPicker: true,
    showEthnicitySelect: false,
    showGarmentTypeSelect: true,
    showFrameScopeSelect: false,
    garmentTypeOptions: MODEL_TRY_ON_GARMENT_TYPE_OPTIONS,
    frameScopeOptions: MODEL_TRY_ON_FRAME_SCOPE_OPTIONS,
    modelTypeOptions: MODEL_TRY_ON_MODEL_TYPE_OPTIONS,
    promptLabel: '补充说明（可选）',
    promptPlaceholder: '可写姿势/搭配/鞋款，如：双手插兜，配白色小白鞋，纯白背景',
    promptRequired: false,
    promptSamples: [
      { label: '电商主图', prompt: '双手插兜，微侧身，纯白背景，柔和自然光' },
      { label: '全身街拍', prompt: '全身入镜，轻度迈步，配小白鞋，都市街拍背景' },
      { label: '商务通勤', prompt: '自然站立，配乐福鞋与西裤，简约灰白背景' },
      { label: '休闲街头', prompt: '单手插兜，配工装裤与马丁靴，街头风' },
    ],
    historyEmptyDescription: '试衣生成后，记录将自动保存在这里',
  },
  modelHandheld: {
    title: '手持商品',
    subtitle: '模特自然手持商品，提升展示真实感',
    generateLabel: '立即生成',
    legalNote: '保持模特姿态与商品外观一致，注意商品与人体真实比例（小件如化妆品尤需准确）',
    layout: 'handheld',
    uploadSlots: [
      { id: 'product', label: '商品图', hint: `白底或透明底商品图，最多 ${MODEL_HANDHELD_MAX_PRODUCTS} 张`, required: true, max: MODEL_HANDHELD_MAX_PRODUCTS, placement: 'workspace' },
    ],
    sidebarSlots: [
      { id: 'model', label: '模特图', hint: '也可在下方选择系统模特', required: false, max: 1, placement: 'sidebar' },
    ],
    showModelPicker: true,
    showEthnicitySelect: true,
    modelTypeOptions: MODEL_TRY_ON_MODEL_TYPE_OPTIONS,
    ethnicityOptions: MODEL_TRY_ON_ETHNICITY_OPTIONS,
    promptLabel: '手持效果（可选）',
    promptPlaceholder: '例如：单手自然托举商品于胸前，微笑看向镜头；化妆品请保持真实小尺寸比例…',
    promptRequired: false,
    promptSamples: [
      { label: '单手展示', prompt: '单手自然托举商品于胸前，微笑看向镜头' },
      { label: '双手捧持', prompt: '双手捧持商品，微侧身体，突出商品细节' },
    ],
    historyEmptyDescription: '手持商品生成后，记录将自动保存在这里',
  },
  modelPoseFission: {
    title: '姿势裂变',
    subtitle: '同一模特同款服装，一键生成多姿势变体',
    generateLabel: '立即裂变',
    legalNote: '基于模特图裂变多种姿势，每张成片仅一种造型，服装与模特身份保持一致',
    layout: 'poseFission',
    uploadSlots: [
      { id: 'model', label: '模特图', hint: `清晰的模特全身或半身照，最多 ${MODEL_POSE_FISSION_MAX_MODELS} 张`, required: true, max: MODEL_POSE_FISSION_MAX_MODELS, placement: 'workspace' },
    ],
    promptLabel: '姿势描述（可选）',
    promptPlaceholder: '例如：侧身站立、走路姿态、双手叉腰、坐姿展示…',
    promptRequired: false,
    promptSamples: [
      { label: '走路姿态', prompt: '自然走路姿态，轻微侧身，动感街拍风格' },
      { label: '坐姿展示', prompt: '优雅坐姿，双腿交叉，手部自然摆放' },
      { label: '侧身站立', prompt: '侧身站立，头部微转看向镜头' },
    ],
    historyEmptyDescription: '姿势裂变生成后，记录将自动保存在这里',
  },
  modelUniversalWear: {
    title: '万物穿戴',
    subtitle: '服装平铺或人台图，一键上身 AI 模特展示',
    generateLabel: '立即穿戴',
    legalNote: '将服装素材智能穿戴到 AI 模特身上，款式与细节保持一致',
    layout: 'universalWear',
    uploadSlots: [
      { id: 'garment', label: '服装图', hint: `平铺、挂拍或人台服装图，最多 ${MODEL_UNIVERSAL_WEAR_MAX_GARMENTS} 张`, required: true, max: MODEL_UNIVERSAL_WEAR_MAX_GARMENTS, placement: 'workspace' },
    ],
    sidebarSlots: [
      { id: 'model', label: '模特图', hint: '也可在下方选择系统模特', required: false, max: 1, placement: 'sidebar' },
    ],
    showModelPicker: true,
    showEthnicitySelect: true,
    modelTypeOptions: MODEL_TRY_ON_MODEL_TYPE_OPTIONS,
    ethnicityOptions: MODEL_TRY_ON_ETHNICITY_OPTIONS,
    promptLabel: '模特风格（可选）',
    promptPlaceholder: '例如：亚洲女性模特，简约室内背景，自然光线…',
    promptRequired: false,
    promptSamples: [
      { label: '简约室内', prompt: '亚洲女性模特，简约室内背景，自然柔光' },
      { label: '户外街拍', prompt: '时尚街拍风格，都市户外背景，自然光线' },
    ],
    historyEmptyDescription: '万物穿戴生成后，记录将自动保存在这里',
  },
};

export function getModelStudioConfig(navId) {
  const base = MODEL_STUDIO_BASE[navId];
  if (!base) return null;
  const images = NAV_HUB_CARD_IMAGES[navId] || {};
  const navMeta = TOOL_NAV_ITEMS[navId];
  const heroImage = images.result || images.source || NAV_HUB_CARD_IMAGES.img2img?.result;
  const sourceImage = images.source || heroImage;
  const sidebarSlots = base.sidebarSlots || [];
  const workspaceSlots = (base.uploadSlots || []).filter((slot) => slot.placement !== 'sidebar');
  const allUploadSlots = [...sidebarSlots, ...workspaceSlots];
  return {
    navId,
    ...base,
    uploadSlots: allUploadSlots,
    workspaceSlots: workspaceSlots.length > 0 ? workspaceSlots : allUploadSlots,
    sidebarSlots,
    title: base.title || navMeta?.label || 'AI模特',
    subtitle: base.subtitle || navMeta?.bannerDescription || '',
    hero: {
      image: heroImage,
      before: images.source,
      caption: navMeta?.cardDesc || base.legalNote,
    },
    tryExamples: [
      {
        id: 'demo-1',
        thumb: sourceImage,
        sampleUrl: sourceImage,
        prompt: base.promptSamples?.[0]?.prompt || '',
      },
      {
        id: 'demo-2',
        thumb: heroImage,
        // 第二张缩略图为效果示意；商品输入仍用 source，与案例 1 区分在手持效果文案（prompt）
        sampleUrl: sourceImage,
        prompt: base.promptSamples?.[1]?.prompt || base.promptSamples?.[0]?.prompt || '',
      },
    ],
    promptMaxLength: MODEL_STUDIO_PROMPT_MAX_LENGTH,
    maxSources: MODEL_STUDIO_MAX_SOURCES,
    generateCountOptions: MODEL_STUDIO_GENERATE_COUNT_OPTIONS,
  };
}
